import APIService from "./APIService";

/**
 * API service for Cyber-Insurance Readiness Reports.
 *
 * Account-scoped reports that aggregate human-layer security controls
 * (MFA, security-awareness training, phishing-simulation results, threat
 * engagement) across all enrolled seats of a corporate account, combined
 * with self-attested administrative controls.
 *
 * Backend base path: /api/v1/app/reporting/cyber-insurance
 * Access: corporate account administrators (account_admin / platform_admin).
 */
const BASE = "/api/v1/app/reporting/cyber-insurance";

class CyberInsuranceReportAPI {
  /**
   * Generate a new cyber-insurance readiness report for the admin's account.
   * @param {Object} options
   * @param {number} [options.periodDays=90] - Trailing window in days.
   * @param {boolean} [options.sendEmail=false] - Whether to email the report.
   * @param {Array<{key:string, attested:boolean, notes?:string}>} [options.attestations]
   * @returns {Promise<Object>} The generated report DTO.
   */
  async generateReport(options = {}) {
    try {
      const requestBody = {
        periodDays: options.periodDays || 90,
        sendEmail: options.sendEmail || false,
      };
      if (Array.isArray(options.attestations)) {
        requestBody.attestations = options.attestations;
      }
      return await APIService.post(`${BASE}/generate`, requestBody);
    } catch (error) {
      console.error(
        "[CyberInsuranceReportAPI] Error generating report:",
        error,
      );
      throw error;
    }
  }

  /**
   * Retrieve a previously generated report by UUID.
   * @param {string} reportUuid
   * @returns {Promise<Object>} The report DTO.
   */
  async getReport(reportUuid) {
    try {
      return await APIService.get(`${BASE}/${reportUuid}`);
    } catch (error) {
      console.error("[CyberInsuranceReportAPI] Error getting report:", error);
      throw error;
    }
  }

  /**
   * List recent reports for the admin's account.
   * @param {number} [limit=20]
   * @returns {Promise<Object>} { reports: [...], count }
   */
  async listReports(limit = 20) {
    try {
      return await APIService.get(`${BASE}/list?limit=${limit}`);
    } catch (error) {
      console.error("[CyberInsuranceReportAPI] Error listing reports:", error);
      throw error;
    }
  }

  /**
   * Get a presigned download URL for the report PDF.
   * @param {string} reportUuid
   * @returns {Promise<Object>} { downloadUrl, expiresInMinutes }
   */
  async getReportPdfUrl(reportUuid) {
    try {
      return await APIService.get(`${BASE}/${reportUuid}/pdf`);
    } catch (error) {
      console.error("[CyberInsuranceReportAPI] Error getting PDF URL:", error);
      throw error;
    }
  }

  /**
   * Permanently delete a previously generated report.
   * @param {string} reportUuid
   * @returns {Promise<void>}
   */
  async deleteReport(reportUuid) {
    try {
      return await APIService.delete(`${BASE}/${reportUuid}`);
    } catch (error) {
      console.error("[CyberInsuranceReportAPI] Error deleting report:", error);
      throw error;
    }
  }

  /**
   * Get color for a readiness score.
   * @param {number} score
   * @returns {string} CSS color value.
   */
  getScoreColor(score) {
    if (score >= 85) return "#10B981"; // Green
    if (score >= 70) return "#3B82F6"; // Blue
    if (score >= 50) return "#F59E0B"; // Amber
    return "#EF4444"; // Red
  }
}

const cyberInsuranceReportAPI = new CyberInsuranceReportAPI();
export default cyberInsuranceReportAPI;
